import React, { useEffect, useState, useRef, memo } from "react";
import PropTypes from "prop-types";
import { 
  trackStoreOpen, 
  trackStoreWebsiteVisit, 
  trackProductView, 
  trackGetDirections 
} from './utils/analytics';
import Fuse from 'fuse.js';
import { loadCanonicalProducts } from './utils/productMatcher';
import { initialize as initializeSyncMatcher, getCanonicalProductSync } from './utils/syncProductMatcher';
import { distance } from './utils/distance';
import LocationBanner from './components/LocationBanner';
import FlavorImage from './components/FlavorImage';
import getProductImagePath from './utils/productImages';
import ModalProductCard from './components/ModalProductCard.jsx';
import ExpandedStoreSection from './components/ExpandedStoreSection';
import ProductsUnavailable from './components/ProductsUnavailable';
import { authenticatedFetch } from './utils/authService';
import './components/StoreProductsLayout.scss';
import './components/FindStoresProducts.scss';
import './components/StoreNameLink.scss'; // Import styles for enhanced store name links

// Helper function to sort array by key (removed from Array.prototype to avoid pollution)
function sortByKey(array, key) {
  return array.sort(function(a, b){
    if(a[key] < b[key]){
      return -1;
    }else if(a[key] > b[key]){
      return 1;
    }
    return 0;
  });
}

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.MODE === 'production' ? 'https://good-feels-store-selector-api.fly.dev' : 'http://localhost:3001');

// When stockOnly is enabled, we normally require a LitAlertsId + confirmed stock.
// Some stores are intentionally allowed to appear even without LitAlerts integration.
const STOCK_ONLY_STORE_EXCEPTIONS = new Set(['terpene journey']);

function isStockOnlyExceptionStore(record) {
  const storeName = record?.fields?.Name;
  if (!storeName) return false;
  return STOCK_ONLY_STORE_EXCEPTIONS.has(String(storeName).trim().toLowerCase());
}

// Move getLocationProducts outside the component to prevent recalculation
// Updated to use backend API instead of frontend filtering
async function getLocationProducts(locationLitAlertsId, useBackendAPI = true) {
  if (!locationLitAlertsId) {
    return [];
  }
  
  // Convert string ID to integer for comparison
  const storeId = parseInt(locationLitAlertsId, 10);
  if (isNaN(storeId)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Invalid LitAlertsId: "${locationLitAlertsId}" is not a valid number`);
    }
    return [];
  }
  
  if (useBackendAPI) {
    try {
      // Use the new backend endpoint for retailer products with authentication
      const response = await authenticatedFetch(`${API_BASE_URL}/api/products/by-retailer/${storeId}`);
      if (!response.ok) {
        console.error(`API error for retailer ${storeId}:`, response.status);
        return [];
      }
      
      const data = await response.json();
      
      // Backend already filters by retailerId and includes all computed fields
      // Filter out products with no stock
      const productsWithStock = (data.data || []).filter(product => {
        // Check if product has configs array with stock
        if (!product.configs || !Array.isArray(product.configs) || product.configs.length === 0) {
          return false;
        }
        // Get the first config (primary variant)
        const config = product.configs[0];
        return config && config.currentStock > 0;
      });
      
      return productsWithStock;
    } catch (error) {
      console.error(`Error fetching products for retailer ${storeId}:`, error);
      return [];
    }
  } else {
    // Fallback to frontend filtering (legacy mode)
    // This would require allProducts to be passed in, but we're removing this
    console.warn('Frontend filtering mode not supported anymore');
    return [];
  }
}

// Helper function to format price as dollar amount
function formatPrice(price) {
  if (typeof price !== 'number') return '';
  return price % 1 === 0 
    ? `$${price}` 
    : `$${price.toFixed(2)}`;
}

// Helper function to format pickup title with limit consideration
function getPickupTitle(locationCount, shopperLocation, limit, isLoadingLocation = false) {
  let city = shopperLocation?.city;
  let state = shopperLocation?.state;
  let location;
  if (!city) {
    location = 'Your Location';
  } else if (state) {
    location = `${city}, ${state}`;
  } else {
    location = city;
  }
  
  // If location is being processed, show loading message
  if (isLoadingLocation) {
    return `Loading pickup and delivery options near ${location}`;
  }
  
  if (limit && locationCount > limit) {
    return `${limit}+ Dispensaries near ${location}`;
  } else {
    return `${locationCount} Dispensaries near ${location}`;
  }
}

// Build a Fuse index for canonical products
let fuse = null;
let canonicalProducts = null;
let initializePromise = null;

// Preload canonical products and Fuse index to avoid async loading issues
async function initializeFuse() {
  if (initializePromise) {
    return initializePromise;
  }

  initializePromise = (async () => {
    if (!fuse || !canonicalProducts) {
      // Preload canonical products eagerly 
      canonicalProducts = await loadCanonicalProducts();
      fuse = new Fuse(canonicalProducts, {
        keys: ['skus', 'aliases'],
        threshold: 0.4, // Adjust for strictness
      });
    }
    return fuse;
  })();
  
  return initializePromise;
}

// Start preloading immediately when this file is imported
Promise.all([
  initializeFuse(),
  initializeSyncMatcher() // Initialize the sync matcher
]).catch(error => {
  console.error('Failed to preload canonical products:', error);
});

// Helper to get canonical info for a product
async function getCanonicalProductInfo(productName) {
  // Try sync lookup first for immediate results
  const syncResult = getCanonicalProductSync(productName);
  if (syncResult) return syncResult;
  
  // Fall back to async lookup if needed
  const searchFuse = await initializeFuse();
  const result = searchFuse.search(productName);
  if (result.length > 0) {
    return result[0].item;
  }
  return null;
}

const Locations = (props) => {
  const [pickupLocations, setPickupLocations] = useState([]);
  const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [errorState, setErrorState] = useState({ hasError: false, message: '' });
  const [retryKey, setRetryKey] = useState(0);
  const [isOutsideServiceArea, setIsOutsideServiceArea] = useState(false);
  const [country, setCountry] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  
  // Get props with defaults first to determine initial display count
  const { 
    stockOnly = true, // Changed default from false to true
    integrated = true,
    showDeliveryOptions = true,
    limit: rawLimit = null,
    infiniteScroll = false
  } = props;
  
  // Normalize limit - handle string "null" and convert to actual null
  const limit = (rawLimit === null || rawLimit === "null" || rawLimit === undefined) ? null : parseInt(rawLimit);
  
  const [displayCount, setDisplayCount] = useState(limit || 10);
  const [products, setProducts] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingStock, setLoadingStock] = useState(false);
  // Remove loadingProducts since we'll load products per-store on demand
  // Replace modal state with expanded section state
  const [expandedStoreId, setExpandedStoreId] = useState(null);
  const [expandedStoreProducts, setExpandedStoreProducts] = useState([]);
  const [loadingStoreProducts, setLoadingStoreProducts] = useState(false);
  
  // Add refs to prevent multiple simultaneous API calls
  const isLocationsFetching = useRef(false);
  const savedScrollPosition = useRef(0);
  
  // Keep track of stores that have stock for initial filtering
  const [storesWithStock, setStoresWithStock] = useState(new Set());
  
  // New state for product filtering
  // const [productNameFilter, setProductNameFilter] = useState('');
  // const [minPrice, setMinPrice] = useState('');
  // const [maxPrice, setMaxPrice] = useState('');
  // const [searchMode, setSearchMode] = useState('exclusive');
  // const [isFilterVisible, setIsFilterVisible] = useState(false);
  // const [filteredProducts, setFilteredProducts] = useState(null);
  const [filteredProducts] = useState(null);
  // const [isFiltering, setIsFiltering] = useState(false);
  
  // Function to update URL with location and selected store
  const updateUrlWithLocation = React.useCallback((store = null, preventScrollUpdate = false) => {
    if (typeof window !== 'undefined') {
      // Get current URL
      const url = new URL(window.location.href);
      
      // Set tab parameter to stores
      url.searchParams.set('tab', 'stores');
      
      // Update the location parameter if we have one
      if (props.shopperLocation?.city) {
        const locationStr = `${props.shopperLocation.city}${props.shopperLocation.state ? `, ${props.shopperLocation.state}` : ''}`;
        url.searchParams.set('location', locationStr);
      } else {
        // Remove the location parameter if empty
        url.searchParams.delete('location');
      }
      
      // Update the store parameter if we have one
      if (store && store.fields && store.fields.LitAlertsId) {
        // Convert to string for URL parameters
        const storeId = String(store.fields.LitAlertsId);
        url.searchParams.set('store', storeId);
        setSelectedStore(storeId);
      } else {
        // Remove the store parameter if no store is selected
        url.searchParams.delete('store');
        setSelectedStore(null);
      }
      
      // Update the URL without refreshing the page or scrolling
      window.history.replaceState({
        location: props.shopperLocation ? props.shopperLocation.city : null,
        store: store ? String(store.fields.LitAlertsId) : null
      }, '', url.toString());
      
      // Only update scroll position if not explicitly prevented
      if (!preventScrollUpdate) {
        // Get the saved scroll position or use current position
        const scrollPosition = parseInt(document.body.getAttribute('data-scroll-position') || window.scrollY || window.pageYOffset, 10);
        
        // Use more reliable scroll restoration with multiple approaches
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
          
          // Double-check after React rendering cycle is complete
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollPosition);
          });
        }, 10);
      }
    }
  }, [props.shopperLocation]);

  // Function to fetch products for a specific store using the backend API
  const fetchStoreProducts = React.useCallback(async (storeId, litAlertsId) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching products for store ${storeId} with LitAlertsId ${litAlertsId}`);
      }
      const storeProducts = await getLocationProducts(litAlertsId, true);
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetched ${storeProducts.length} products for store ${storeId}`);
      }
      return storeProducts;
    } catch (error) {
      console.error('Error fetching store products:', error);
      return [];
    }
  }, []); // Remove loadingStoreProducts from dependencies to prevent recreation

  // Function to check if a store has stock (for initial filtering)
  const checkStoreHasStock = React.useCallback(async (litAlertsId) => {
    try {
      const storeProducts = await getLocationProducts(litAlertsId, true);
      return storeProducts.length > 0;
    } catch (error) {
      console.error(`Error checking stock for store ${litAlertsId}:`, error);
      return false;
    }
  }, []);

  // Function to check stock for multiple stores at once
  const checkStoresStock = React.useCallback(async (locations) => {
    if (!stockOnly) {
      // If not filtering by stock, all stores are considered to have stock
      return new Set(locations.map(loc => loc.id));
    }

    const storesWithStockSet = new Set();
    
    // Get all LitAlertsIds from the locations
    const litAlertsIds = locations
      .map(location => location.fields?.LitAlertsId)
      .filter(id => id && !isNaN(parseInt(id, 10)))
      .map(id => parseInt(id, 10));
    
    if (litAlertsIds.length === 0) {
      return storesWithStockSet;
    }
    
    try {
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log(`Checking stock for ${litAlertsIds.length} stores using batch API`);
      }
      
      // Split into chunks of 50 stores to respect API limits
      const BATCH_SIZE = 50;
      const chunks = [];
      for (let i = 0; i < litAlertsIds.length; i += BATCH_SIZE) {
        chunks.push(litAlertsIds.slice(i, i + BATCH_SIZE));
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Split into ${chunks.length} batches of max ${BATCH_SIZE} stores each`);
      }
      
      // Process all chunks in parallel
      const chunkPromises = chunks.map(async (chunk, index) => {
        try {
          const response = await authenticatedFetch(`${API_BASE_URL}/api/stores/check-stock`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ storeIds: chunk })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Batch ${index + 1} error:`, errorText);
            throw new Error(`Batch ${index + 1} failed: ${response.status}`);
          }
          
          const stockData = await response.json();
          return stockData.storesWithStock || [];
        } catch (error) {
          console.error(`Error in batch ${index + 1}:`, error);
          return []; // Return empty array for failed batches
        }
      });
      
      // Wait for all batches to complete
      const batchResults = await Promise.all(chunkPromises);
      
      // Combine all results
      const allStoresWithStock = batchResults.flat();
      const storesWithStockLitAlertsIds = new Set(allStoresWithStock);
      
      // Map LitAlertsIds back to location IDs
      locations.forEach(location => {
        const litAlertsId = parseInt(location.fields?.LitAlertsId, 10);
        if (!isNaN(litAlertsId) && storesWithStockLitAlertsIds.has(litAlertsId)) {
          storesWithStockSet.add(location.id);
        }
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Batch stock check completed: ${storesWithStockSet.size} stores with stock out of ${locations.length} total`);
      }
      
    } catch (error) {
      console.error('Error in batch stock check:', error);
      // Fallback to individual checks if batch fails
      const stockChecks = locations.map(async (location) => {
        const litAlertsId = location.fields?.LitAlertsId;
        if (litAlertsId) {
          const hasStock = await checkStoreHasStock(litAlertsId);
          if (hasStock) {
            storesWithStockSet.add(location.id);
          }
        }
      });
      
      await Promise.all(stockChecks);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fallback stock check: ${storesWithStockSet.size} stores with stock out of ${locations.length} total`);
      }
    }
    
    // If we had stores to check but got zero results, the stock API is likely down
    if (storesWithStockSet.size === 0 && litAlertsIds.length > 0) {
      const err = new Error('Stock API returned no results for any store');
      err.isStockApiDown = true;
      throw err;
    }
    
    return storesWithStockSet;
  }, [stockOnly, checkStoreHasStock]);

  // Define toggleExpandStore to replace handleOpenModal - now uses backend API
  const toggleExpandStore = React.useCallback(async (record, skipProductsFetch = false) => {
    if (!record || !record.fields) {
      console.error('Attempted to toggle store with invalid record:', record);
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('toggleExpandStore called:', {
        storeName: record.fields.Name,
        storeId: record.id,
        litAlertsId: record.fields.LitAlertsId,
        skipProductsFetch,
        currentExpandedId: expandedStoreId
      });
    }
    
    // If this store is already expanded, collapse it
    if (expandedStoreId === record.id) {
      setExpandedStoreId(null);
      setExpandedStoreProducts([]);
      
      // Update URL (remove store)
      updateUrlWithLocation(null, true); // preventScrollUpdate = true
    } else {
      // Collapse any currently expanded store and expand this one
      setExpandedStoreId(record.id);
      
      // Track analytics event
      trackStoreOpen(record);
      
      // Update URL with store ID for deep linking
      updateUrlWithLocation(record, true); // preventScrollUpdate = true
      
      // IMMEDIATELY show the expanded section with loading state (for better INP)
      setExpandedStoreProducts([]); // Start with empty array to show loading
      setLoadingStoreProducts(true); // Set loading state
      
      // Fetch products using the backend API if not skipping (AFTER showing UI)
      if (!skipProductsFetch && record.fields.LitAlertsId) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log('Fetching products for expanded store:', record.fields.Name);
          }
          const storeProducts = await fetchStoreProducts(record.id, record.fields.LitAlertsId);
          if (process.env.NODE_ENV === 'development') {
            console.log('Setting expanded store products:', storeProducts.length, 'products');
          }
          setExpandedStoreProducts(storeProducts);
        } catch (error) {
          console.error('Error fetching products for expanded store:', error);
          setExpandedStoreProducts([]);
        } finally {
          setLoadingStoreProducts(false); // Always clear loading state
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Skipping product fetch:', { skipProductsFetch, hasLitAlertsId: !!record.fields.LitAlertsId });
        }
        setExpandedStoreProducts([]);
        setLoadingStoreProducts(false); // Clear loading state when skipping fetch
      }
    }
  }, [expandedStoreId, updateUrlWithLocation, fetchStoreProducts]);
  
  // The handleOpenModal function has been replaced by toggleExpandStore
  
  // Add Escape key handler for expanded sections
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && expandedStoreId) {
        console.log('Escape key pressed, collapsing expanded store section');
        setExpandedStoreId(null);
        setExpandedStoreProducts([]);
        updateUrlWithLocation(null);
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [expandedStoreId, updateUrlWithLocation]);

  // Memoize all filter string calculations to prevent re-computation
  const { rangeSearchString, deliveryAreaSearchString } = React.useMemo(() => {
    let rangeSearch = '';
    let deliveryAreaSearch = '';

    // Filter for pickup locations - by geographic proximity
    if (props.shopperLocation) {
      let lat1 = parseFloat(props.shopperLocation.lat, 10);
      lat1 = lat1 - 0.5;
      let lat2 = lat1 + 1.0;
      let lng1 = parseFloat(props.shopperLocation.lng, 10);
      lng1 = lng1 - 0.5;
      let lng2 = lng1 + 1.0;
      rangeSearch = ',{Lat}>' + lat1 + ',{Lat}<' + lat2 + ',{Lng}>' + lng1 + ',{Lng}<' + lng2;
    }

    // Filter for delivery locations - by city name in delivery areas
    if (props.shopperLocation?.city) {
      const cityInput = props.shopperLocation.city.trim();
      let cityName = cityInput;
      
      if (cityInput.includes(' ')) {
        const parts = cityInput.split(' ');
        cityName = parts[parts.length - 1];
      }
      
      if (props.shopperLocation?.state) {
        const stateCode = props.shopperLocation.state.trim();
        const cityState = `${cityName}, ${stateCode}`;
        const fullCityState = `${cityInput}, ${stateCode}`;
        
        deliveryAreaSearch = `,OR(
          FIND("${cityInput}", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND("${cityName}", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND("${cityName},", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND(",${cityName},", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND(",${cityName}", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND("${cityState}", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND("${fullCityState}", ARRAYJOIN({DeliveryAreasLookup}, ","))
        )`;
      } else {
        deliveryAreaSearch = `,OR(
          FIND("${cityInput}", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND("${cityName},", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND(",${cityName},", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND(",${cityName}", ARRAYJOIN({DeliveryAreasLookup}, ",")),
          FIND("${cityName}", ARRAYJOIN({DeliveryAreasLookup}, ","))
        )`;
      }
      
      // if (process.env.NODE_ENV === 'development') { 
      //   console.log(`Generated delivery area search for ${cityInput}: ${deliveryAreaSearch}`);
      // }
    }

    return { rangeSearchString: rangeSearch, deliveryAreaSearchString: deliveryAreaSearch };
  }, [props.shopperLocation?.city, props.shopperLocation?.state, props.shopperLocation?.lat, props.shopperLocation?.lng]);

  // Check if user is outside service area (Massachusetts or more than 100 miles from any dispensary)
  useEffect(() => {
    if (!props.shopperLocation) return;

    // Set country (we assume US for now, but this could be updated with IP-based geo service)
    const userCountry = props.shopperLocation.country || 'US';
    setCountry(userCountry);
    
    // If outside US, we're definitely outside service area
    if (userCountry && userCountry !== 'US' && userCountry !== 'USA') {
      setIsOutsideServiceArea(true);
      return;
    }
    
    // If in Massachusetts, we're in the service area
    if (props.shopperLocation.state === 'MA') {
      setIsOutsideServiceArea(false);
      return;
    }
    
    // Otherwise, check distance to nearest store
    if (pickupLocations.length > 0) {
      let nearestDistance = Infinity;
      
      pickupLocations.forEach(location => {
        // Skip locations with invalid coordinates
        if (!location.fields || !location.fields.Lat || !location.fields.Lng) {
          return;
        }
        
        const storeCoords = {
          lat: parseFloat(location.fields.Lat),
          lng: parseFloat(location.fields.Lng)
        };
        
        // Skip if we couldn't parse valid coordinates
        if (isNaN(storeCoords.lat) || isNaN(storeCoords.lng)) {
          return;
        }
        
        const dist = distance(props.shopperLocation, storeCoords);
        if (dist !== null && dist < nearestDistance) {
          nearestDistance = dist;
        }
      });
      
      // Outside service area if more than 100 miles away
      setIsOutsideServiceArea(nearestDistance > 100);
    }
  }, [props.shopperLocation, pickupLocations]);

  // Memoize the filter formulas to prevent unnecessary useEffect re-runs
  const { pickupFilter, deliveryFilter } = React.useMemo(() => {
    return {
      pickupFilter: 'AND({Pickup}=1' + rangeSearchString + ',{Visible}=1)',
      deliveryFilter: 'AND({Delivery}=1' + deliveryAreaSearchString + ',{Visible}=1)'
    };
  }, [rangeSearchString, deliveryAreaSearchString]);
  
  // Memoize the fields string to prevent useEffect re-runs
  const fields = React.useMemo(() => {
    return "fields[]=Name&fields[]=DeliveryURL&fields[]=PickupURL&fields[]=City&fields[]=State&fields[]=Lat&fields[]=Lng&fields[]=DeliveryAreasLookup&fields[]=Image&fields[]=LitAlertsId";
  }, []);

  // Check stock for stores when locations are loaded
  useEffect(() => {
    if (pickupLocations.length > 0 || deliveryLocations.length > 0) {
      const allLocations = [...pickupLocations, ...deliveryLocations];
      setLoadingStock(true);
      checkStoresStock(allLocations)
        .then((stockSet) => {
          setStoresWithStock(stockSet);
          setLoadingStock(false);
        })
        .catch((err) => {
          console.error('Stock check failed:', err);
          setLoadingStock(false);
          if (err.isStockApiDown) {
            setErrorState({
              hasError: true,
              message: 'Unable to load product availability. Please try again later.'
            });
          }
        });
    }
  }, [pickupLocations, deliveryLocations, checkStoresStock]);
  
  // Update URL with location when shopper location is auto-detected
  useEffect(() => {
    // Only update if we have a valid location and we're not already processing a deep link
    if (props.shopperLocation?.city && !window.location.search.includes('location=')) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Auto-detected location, updating URL:', props.shopperLocation.city);
      }
      updateUrlWithLocation();
    }
  }, [props.shopperLocation, updateUrlWithLocation]);

  // Handle deep linking via URL parameters
  useEffect(() => {
    // Parse URL parameters for location and store
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const locationParam = urlParams.get('location');
      const storeParam = urlParams.get('store');
      if (process.env.NODE_ENV === 'development') {
        console.log('Deep linking parameters detected:', { locationParam, storeParam });
      }

      // If store ID is provided, we'll select it after locations load
      if (storeParam) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Setting selected store from URL parameter:', storeParam, 'Type:', typeof storeParam);
        }
        // Store as string to maintain consistency
        setSelectedStore(storeParam);
      }

      // Log the current location situation with the deep link
      if (locationParam && process.env.NODE_ENV === 'development') {
        console.log('Deep link location specified:', locationParam);
        
        // The location from deep link should have been handled in index.js
        // Just log the current state
        if (props.shopperLocation?.city) {
          console.log('Current shopper location being used:', {
            city: props.shopperLocation.city,
            state: props.shopperLocation.state,
            source: 'Deep link from URL'
          });
        } else {
          console.log('Waiting for shopper location to be set from deep link');
        }
      }
      
      // If URL has 'tab=stores' but no location parameter, and we have a location,
      // update the URL to include the location
      if (urlParams.has('tab') && urlParams.get('tab') === 'stores' && 
          !urlParams.has('location') && props.shopperLocation?.city) {
        if (process.env.NODE_ENV === 'development') {         
          console.log('Tab is stores but no location in URL, updating with current location');
        }
        updateUrlWithLocation();
      }
    }
  }, [props.shopperLocation, updateUrlWithLocation]);
  
  // Dedicated effect to expand a store section when direct link to a store
  useEffect(() => {
    // Only proceed if:
    // 1. We have a selectedStore from URL
    // 2. Locations are loaded
    // 3. Products are loaded
    // 3. No store is currently expanded
    if (selectedStore && !loadingLocations && !expandedStoreId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Deep linking debug:', {
          selectedStore,
          selectedStoreType: typeof selectedStore,
          loadingLocations,
          hasExpandedStore: !!expandedStoreId,
          pickupLocationsCount: pickupLocations.length,
          deliveryLocationsCount: deliveryLocations.length
        });
      }
      
      // Look for the store in both pickup and delivery locations
      const allLocations = [...pickupLocations, ...deliveryLocations];
      
      // Convert selectedStore to number for proper comparison
      const selectedStoreId = parseInt(selectedStore, 10);
      if (process.env.NODE_ENV === 'development') {
        console.log('Searching for store ID:', selectedStoreId, 'Type:', typeof selectedStoreId);
      
        // Log all available LitAlertsIds for debugging
        console.log('Available LitAlertsIds:', allLocations.map(loc => {
          return {
            id: loc.fields.LitAlertsId,
            type: typeof loc.fields.LitAlertsId,
            name: loc.fields.Name
          };
        }));
      }

      const storeRecord = allLocations.find(
        record => Number(record.fields.LitAlertsId) === selectedStoreId
      );
      if (process.env.NODE_ENV === 'development') {
        console.log('Store found?', !!storeRecord, storeRecord ? storeRecord.fields.Name : 'Not found');
      }
      
      if (storeRecord) {
        // Expand this store with a slight delay to ensure the component is fully rendered
        // Products will be fetched inside toggleExpandStore
        setTimeout(async () => {
          // Explicitly call toggleExpandStore and ensure products are fetched
          if (process.env.NODE_ENV === 'development') {
            console.log('Deep linking: expanding store and fetching products for:', storeRecord.fields.Name);
          }
          await toggleExpandStore(storeRecord, false); // Explicitly pass false to ensure products are fetched
          
          // Scroll to the store
          setTimeout(() => {
            const storeElement = document.getElementById(`store-${storeRecord.id}`);
            if (storeElement) {
              storeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }, 300); // Increased delay for better reliability
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Store with ID ${selectedStore} not found in loaded locations. Available IDs:`, 
            allLocations.map(loc => loc.fields.LitAlertsId).filter(Boolean));
        }
      }
    }
  }, [
    selectedStore, 
    loadingLocations, 
    expandedStoreId, 
    pickupLocations, 
    deliveryLocations, 
    toggleExpandStore
  ]);

  useEffect(() => {
    // STRONG guard: prevent multiple simultaneous location fetches
    if (isLocationsFetching.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🚫 BLOCKED: Already fetching locations, ignoring duplicate request');
      }
      return;
    }
    
    // NEW: Don't fetch locations until we have a resolved shopper location
    // This prevents the double API call issue
    if (!props.shopperLocation?.city) {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏳ WAITING: No shopper location yet, skipping location fetch');
      }
      return;
    }
    
    isLocationsFetching.current = true;
    setLoadingLocations(true);

    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Location search for:', {
        city: props.shopperLocation?.city,
        lat: props.shopperLocation?.lat,
        lng: props.shopperLocation?.lng,
        integrated: integrated
      });
    }

    // Helper function to fetch locations with a given filter
    const fetchLocations = async (filter, isPickup = true) => {
      const params = new URLSearchParams({
        fields: fields,
        filter: filter
      });
      
      // For delivery requests, also send the city directly as a parameter
      // This helps the server identify delivery areas even if the filter formula doesn't contain it
      if (!isPickup && props.shopperLocation?.city) {
        const city = props.shopperLocation.city;
        const state = props.shopperLocation.state;
        
        // Only add parameters if they are actually defined
        if (city && city !== 'undefined') {
          params.append('city', city);
        }
        
        if (state && state !== 'undefined') {
          params.append('state', state);
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching ${isPickup ? 'pickup' : 'delivery'} locations with filter:`, filter);
        if (!isPickup) {
          const city = props.shopperLocation?.city;
          const state = props.shopperLocation?.state;
          
          console.log(`DELIVERY DEBUG - Shopper location:`, props.shopperLocation);
          
          if (city && city !== 'undefined') {
            console.log(`Adding city=${city} to params`);
          } else {
            console.log('City parameter not added: undefined or invalid');
          }
          
          if (state && state !== 'undefined') {
            console.log(`Adding state=${state} to params`);
          } else {
            console.log('State parameter not added: undefined or invalid');
          }
          
          // Log the full URL that will be requested
          console.log(`Full delivery locations request URL: ${API_BASE_URL}/api/locations?${params.toString()}`);
        }
      }
      
      try {
        const res = await authenticatedFetch(`${API_BASE_URL}/api/locations?${params}`);
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Locations API error:', res.status, errorText);
          throw new Error(`API error: ${res.status}`);
        }
        const data = await res.json();
        if (process.env.NODE_ENV === 'development') {
          console.log(`Received ${isPickup ? 'pickup' : 'delivery'} locations:`, data.records.length);
        }
        return isPickup ? sortByDistance(data.records, props.shopperLocation) : data.records;
      } catch (error) {
        console.error(`Error fetching ${isPickup ? 'pickup' : 'delivery'} locations:`, error);
        setErrorState({
          hasError: true,
          message: 'Unable to load store locations. Please try again later.'
        });
        return [];
      }
    };

    // For integrated view, fetch both types of locations in parallel
    if (integrated) {
      // Always fetch delivery options - enforce this to be true
      const enforceDeliveryOptions = true;
      if (process.env.NODE_ENV === 'development') {
        console.log(`Fetching locations with delivery options ENABLED`);
      }
      
      Promise.all([
        fetchLocations(pickupFilter, true),
        showDeliveryOptions ? fetchLocations(deliveryFilter, false) : Promise.resolve([])
      ])
        .then(([pickupResults, deliveryResults]) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('API returned pickup locations:', pickupResults.length);
            console.log('API returned delivery locations:', deliveryResults.length);
            if (deliveryResults.length > 0) {
              console.log('FOUND DELIVERY LOCATIONS FROM API:');
              deliveryResults.forEach(loc => {
                console.log(`- ${loc.fields?.Name} (${loc.fields?.City || 'Unknown'}, ${loc.fields?.State || 'Unknown'}) - DeliveryURL: ${loc.fields?.DeliveryURL || 'Missing'}`);
              });
            } else {
              console.log('No delivery locations found.');
            }
          }
          setPickupLocations(pickupResults);
          setDeliveryLocations(deliveryResults);
          setLoadingLocations(false);
          isLocationsFetching.current = false; // Reset flag
        })
        .catch(error => {
          console.error('Error in parallel location fetching:', error);
          setLoadingLocations(false);
          isLocationsFetching.current = false; // Reset flag
        });
    } 
    // For legacy mode, use the existing approach based on pickupOrDelivery
    else if (props.pickupOrDelivery) {
      const isPickup = props.pickupOrDelivery === 'pickup';
      const filter = isPickup ? pickupFilter : deliveryFilter;
      
      fetchLocations(filter, isPickup)
        .then(locations => {
          if (isPickup) {
            setPickupLocations(locations);
            setDeliveryLocations([]);
          } else {
            setPickupLocations([]);
            setDeliveryLocations(locations);
          }
          setLoadingLocations(false);
          isLocationsFetching.current = false; // Reset flag
        })
        .catch(error => {
          console.error('Error fetching locations:', error);
          setLoadingLocations(false);
          isLocationsFetching.current = false; // Reset flag
        });
    }
  }, [
    // Core shopper location dependencies
    props.shopperLocation?.city,
    props.shopperLocation?.lat,
    props.shopperLocation?.lng,
    props.shopperLocation?.state,
    // Configuration dependencies 
    props.pickupOrDelivery,
    integrated,
    showDeliveryOptions,
    // Now include the memoized filters since they're stable
    pickupFilter,
    deliveryFilter,
    fields,
    retryKey // Allows manual retry after error
    // IMPORTANT: Do NOT include selectedStore, products, modalData, modalIsOpen
    // or handleOpenModal to prevent refetching when modal opens
  ]);

  function sortByDistance(records, shopperLocation) {
    // Always require valid coordinates for distance calculations
    if (!shopperLocation || 
        (!shopperLocation.Lat && !shopperLocation.lat) || 
        !Number.isFinite(shopperLocation.lat) || !Number.isFinite(shopperLocation.lng)) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Invalid shopper location for distance sorting:', shopperLocation);
        console.error('This should never happen - location should always have valid coordinates');
      }
      // This should never happen with our new fallback logic, but just in case
      return records;
    }
    
    // calculate distances based on location
    records.forEach((record) => {
      if (!record.hasOwnProperty("distance") || record._lastDistanceLocation !== shopperLocation) {
        try {
          const dist = distance(record.fields, shopperLocation);
          record.distance = dist;
          if (record.fields) {
            record.fields.distance = dist;
          }
          record._lastDistanceLocation = shopperLocation;
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error calculating distance for record:', record.id, error);
          }
          record.distance = null;
          if (record.fields) {
            record.fields.distance = null;
          }
        }
      }
    });
    
    // Filter out records with null/NaN distance
    const validRecords = records.filter(r => r.distance !== null && Number.isFinite(r.distance));
    sortByKey(validRecords, 'distance');
    return validRecords;
  }

  // Simplified product mapping - now just tracks which stores have stock
  // Actual products are fetched on-demand when expanding store sections
  const locationProductsMap = React.useMemo(() => {
    const map = new Map();
    const allLocations = [...pickupLocations, ...deliveryLocations];
    
    // For stores with confirmed stock, mark them as having products
    allLocations.forEach(record => {
      if (record.fields.LitAlertsId) {
        const hasStock = storesWithStock.has(record.id);
        // Store a placeholder array with length indication for stock-based filtering
        map.set(record.fields.LitAlertsId, hasStock ? [{ placeholder: true }] : []);
      }
    });
    
    return map;
  }, [pickupLocations, deliveryLocations, storesWithStock]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event) => {
      // Get search params from the URL when user navigates back/forward
      const urlParams = new URLSearchParams(window.location.search);
      const storeParam = urlParams.get('store');
      if (process.env.NODE_ENV === 'development') { 
        console.log('Browser navigation detected, store param:', storeParam);
      }
      
      // Close any expanded store when navigating away from a store
      if (!storeParam && expandedStoreId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Collapsing expanded store due to browser navigation');
        }
        setExpandedStoreId(null);
        setExpandedStoreProducts([]);
      }
      
      // Update selected store without triggering a new URL change
      setSelectedStore(storeParam);
    };
    
    // Add event listener for browser navigation
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [expandedStoreId]);

  // Memoized LocationImage component
  const LocationImage = memo(function LocationImage({ recordImage, recordName }) {
    const hasImages = Array.isArray(recordImage) && recordImage.length > 0;
    const imageData = hasImages ? recordImage[0] : null;
    const imageUrl =
      imageData?.thumbnails?.large?.url ||
      imageData?.thumbnails?.full?.url ||
      imageData?.url ||
      null;

    if (!imageUrl && process.env.NODE_ENV === 'development' && hasImages) {
      console.warn('Location image is missing expected thumbnail sizes:', recordName, imageData);
    }

    if (imageUrl) {
      return (
        <div className="store-image">
          <img
            src={imageUrl}
            alt={`${recordName || 'Store'} store location`}
          />
        </div>
      );
    }

    return <div className="store-image no-image"></div>;
  });

  LocationImage.propTypes = {
    recordImage: PropTypes.array,
    recordName: PropTypes.string
  };

  // ExpandedStoreSection component is now imported at the top of the file

  // ProductModal has been removed in favor of expandable sections

  // Update the LocationRow component to include expandable functionality
  const LocationRow = React.memo(({ 
    record, 
    type, 
    hasStock, 
    products, 
    matchedProductCount,
    isExpanded,
    isLoadingProducts,
    onToggleExpand
  }) => {
    const url = type === 'delivery' ? record.fields.DeliveryURL : record.fields.PickupURL;
    
    const handleRowClick = (e) => {
      // Prevent default to avoid page scrolling
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      
      // No action on row click as we now use the button for expansion
    };

    const handleButtonClick = (e) => {
      e.stopPropagation(); // Prevent row click
      
      if (hasStock) {
        // Toggle expanded state - don't pass products array as second parameter!
        onToggleExpand(record);
      } else {
        trackStoreWebsiteVisit(record, type);
        window.open(url, '_blank');
      }
    };

    return (
      <div 
        className={`store-row ${type} ${hasStock ? 'has-stock' : ''} ${matchedProductCount ? 'has-matches' : ''} ${isExpanded ? 'expanded' : ''}`}
        id={`store-${record.id}`} // Add ID for scrolling to this store
      >
        <div className="store-content" onClick={(e) => handleRowClick(e)}>
          <LocationImage recordImage={record.fields['Image']} recordName={record.fields.Name} />
          <div className="store-info">
            <span className="store-name">{record.fields.Name}</span>
            {type === 'delivery' ? (
              <span className="delivery-areas">
                Delivers to: {record.fields.DeliveryAreasLookup && record.fields.DeliveryAreasLookup.join(', ')}
              </span>
            ) : (
              <span className="distance-info">
                {record.fields.City}, {record.fields.State} - {record.distance && record.distance.toLocaleString(undefined, {maximumFractionDigits:2})} miles away
              </span>
            )}
            {hasStock && (
              <button 
                className="view-stock-button"
                onClick={handleButtonClick}
              >
                {isExpanded ? 'Hide Stock' : 
                  (matchedProductCount ? 
                    `View Stock (${matchedProductCount} matched)` : 
                    'View Stock')}
              </button>
            )}
            {!hasStock && (
              <button 
                className="visit-website-button"
                onClick={handleButtonClick}
              >
                {type === 'delivery' ? 'Visit for Delivery' : 'Visit Website'}
              </button>
            )}
          </div>
        </div>
        
        <div className="expanded-section-container">
          {isExpanded && hasStock && (
            <ExpandedStoreSection
              products={products}
              location={record}
              loading={isLoadingProducts}
              onClose={() => onToggleExpand(record)}
            />
          )}
        </div>
      </div>
    );
  });

  LocationRow.propTypes = {
    record: PropTypes.object.isRequired,
    type: PropTypes.string.isRequired,
    hasStock: PropTypes.bool.isRequired,
    products: PropTypes.array,
    matchedProductCount: PropTypes.number,
    isExpanded: PropTypes.bool,
    isLoadingProducts: PropTypes.bool,
    onToggleExpand: PropTypes.func.isRequired
  };

  // Update the renderLocationRow function to handle expandable sections
  function renderLocationRow(record, type) {
    // Check if this location has stock using our stock tracking
    const hasStock = storesWithStock.has(record.id);
    
    // Check if this store is currently expanded
    const isExpanded = expandedStoreId === record.id;
    
    // Use the actual expanded store products if this store is expanded
    const storeProducts = isExpanded ? expandedStoreProducts : [];
    
    // Check if we're currently loading products for this store
    const isLoadingProducts = isExpanded && loadingStoreProducts;
    
    return (
      <LocationRow 
        key={record.id}
        record={record}
        type={type}
        hasStock={hasStock}
        products={storeProducts}
        matchedProductCount={0} // We'll remove this or calculate it differently
        isExpanded={isExpanded}
        isLoadingProducts={isLoadingProducts}
        onToggleExpand={toggleExpandStore}
      />
    );
  }

  // Add scroll handler
  useEffect(() => {
    // Don't set up scroll handler if infiniteScroll is disabled
    if (!infiniteScroll) {
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('Infinite scroll is disabled. Setting displayCount to all records:', pickupLocations.length);
      // }
      // If infiniteScroll is disabled but we have locations, show them all
      if (pickupLocations.length > 0) {
        setDisplayCount(pickupLocations.length);
      }
      return;
    }
    
    // Use ref to avoid global namespace pollution
    let scrollTimeoutRef = null;
    
    function handleScroll() {
      const container = document.getElementById('list-of-locations');
      if (!container && process.env.NODE_ENV === 'development') {
        console.warn('List of locations container not found');
        return;
      }
      
      // Calculate the effective total that can be displayed
      const stockFiltered = stockOnly 
        ? pickupLocations.filter(record => {
            if (isStockOnlyExceptionStore(record)) return true;
            const litAlertsId = record.fields.LitAlertsId;
            if (!litAlertsId) return false;
            const locationProducts = locationProductsMap.get(litAlertsId) || [];
            return locationProducts.length > 0;
          })
        : pickupLocations;
      
      // Calculate total available locations (no artificial limits for scroll handler)
      const totalLocations = integrated ? (stockFiltered.length + deliveryLocations.length) : stockFiltered.length;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Scroll handler - displayCount:', displayCount, 'totalLocations:', totalLocations);
      }
      
      // Prevent loading more if we're already at the end
      if (displayCount >= totalLocations) return;

      // Get container's bounding rectangle
      const rect = container.getBoundingClientRect();
      
      // More generous check for bottom visibility - trigger loading when approaching bottom
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const isNearBottom = rect.bottom <= windowHeight + 200; // Load more when within 200px of bottom
      
      // Debounce the scroll handler
      if (scrollTimeoutRef) {
        clearTimeout(scrollTimeoutRef);
      }

      scrollTimeoutRef = setTimeout(() => {
        if (isNearBottom) {
          // Use limit as page size if specified, otherwise default to 10
          const pageSize = limit || 10;
          const newDisplayCount = Math.min(displayCount + pageSize, totalLocations);
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`Loading more items: ${displayCount} -> ${newDisplayCount} (of ${totalLocations})`);
          }
          
          setDisplayCount(newDisplayCount);
        }
      }, 100);
    }

    // Attach scroll event listener
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('scroll', handleScroll); // For some environments that don't trigger window.scroll
    
    // Initial check after component mounts to handle case where content doesn't fill the page
    setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Initial scroll check - displayCount:', displayCount);
      }
      handleScroll();
    }, 500);
    
    return () => {
      if (scrollTimeoutRef) {
        clearTimeout(scrollTimeoutRef);
      }
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [
    pickupLocations, // Include the full array to detect any changes
    deliveryLocations, 
    displayCount, 
    infiniteScroll, 
    stockOnly, 
    limit, 
    integrated, 
    storesWithStock
  ]);

  // Create IntegratedView component for showing both delivery and pickup options
  const IntegratedView = React.memo(function IntegratedView() {
    // Since we no longer load all products upfront, we can render immediately
    // Stock checking happens in the background
    
    // Only show debug info in development (memoized to prevent repeated logs)
    const debugData = React.useMemo(() => {
      const data = {
        pickup: pickupLocations.length,
        delivery: deliveryLocations.length,
        products: products?.data?.length || 0,
        city: props.shopperLocation?.city,
        state: props.shopperLocation?.state
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🔍 IntegratedView Debug - Pickup: ${data.pickup}, Delivery: ${data.delivery}, Products: ${data.products}, Location: ${data.city}, ${data.state}`);
        
        // Debug delivery locations
        if (deliveryLocations.length > 0) {
          console.log('Delivery locations available:', deliveryLocations.map(loc => ({
            name: loc.fields.Name,
            city: loc.fields.City,
            state: loc.fields.State,
            deliveryAreas: loc.fields.DeliveryAreasLookup || 'None listed',
            hasDeliveryURL: !!loc.fields.DeliveryURL
          })));
        }
      }
      
      return data;
    }, [pickupLocations.length, deliveryLocations.length, products?.data?.length, props.shopperLocation?.city, props.shopperLocation?.state]);
    
    // Filter pickup locations based on stockOnly setting
    const stockFilteredPickupLocations = stockOnly 
      ? pickupLocations.filter(record => {
          if (isStockOnlyExceptionStore(record)) return true;
          // Only show locations that have products (stock)
          const litAlertsId = record.fields.LitAlertsId;
          if (!litAlertsId) return false;
          
          const locationProducts = locationProductsMap.get(litAlertsId) || [];
          return locationProducts.length > 0;
        })
      : pickupLocations;
    
    // Apply limit and infiniteScroll logic
    let filteredPickupLocations;
    if (limit && !infiniteScroll) {
      // Show exactly 'limit' number of stores and stop
      filteredPickupLocations = stockFilteredPickupLocations.slice(0, limit);
    } else if (limit && infiniteScroll) {
      // Infinite scroll with 'limit' as page size - show displayCount number of results
      filteredPickupLocations = stockFilteredPickupLocations.slice(0, displayCount);
    } else if (!limit && infiniteScroll) {
      // No limit + infinite scroll = show ALL stores immediately
      filteredPickupLocations = stockFilteredPickupLocations;
    } else {
      // No limit + fixed count = show ALL stores
      filteredPickupLocations = stockFilteredPickupLocations;
    }
    
    // Check if we have any delivery options for this location
    // Apply stockOnly filtering to delivery locations as well
    const stockFilteredDeliveryLocations = stockOnly 
      ? deliveryLocations.filter(record => {
          // Only show delivery locations that have products (stock)
          if (!record.fields.Delivery) return false; // Must have Delivery=true
          
          if (isStockOnlyExceptionStore(record)) return true;
          const litAlertsId = record.fields.LitAlertsId;
          if (!litAlertsId) return false;
          
          const locationProducts = locationProductsMap.get(litAlertsId) || [];
          return locationProducts.length > 0;
        })
      : deliveryLocations.filter(loc => loc.fields.Delivery);
    
    const hasDeliveryOptions = stockFilteredDeliveryLocations.length > 0;
    
    return (
      <div className="integrated-location-view">
        {hasDeliveryOptions && (
          <div className="delivery-section">
            <h3>Delivery Options for {props.shopperLocation?.city || 'Your Location'}</h3>
            {stockFilteredDeliveryLocations.slice(0, displayCount).map(record => 
              renderLocationRow(record, 'delivery')
            )}
            {displayCount < stockFilteredDeliveryLocations.length && (
              <div className="loading-more">Loading more delivery options...</div>
            )}
          </div>
        )}
        
        <div className="pickup-section">
          <h3>{getPickupTitle(stockFilteredPickupLocations.length, props.shopperLocation, limit, 
            loadingLocations || loadingStock || (!loadingLocations && stockFilteredPickupLocations.length === 0 && props.shopperLocation?.city))}</h3>
          {filteredPickupLocations.map(record => 
            record.fields.PickupURL && renderLocationRow(record, 'pickup')
          )}
          {infiniteScroll && displayCount < stockFilteredPickupLocations.length && (
            <div className="loading-more">Loading more pickup locations...</div>
          )}
        </div>
      </div>
    );
  });

  // Update PickupOptions to support stockOnly filter
  function PickupOptions() {
    // Since we no longer load all products upfront, we can render immediately
    // Stock checking happens in the background
    
    // Only show debug info in development
    if (process.env.NODE_ENV === 'development') {
      // console.log(`StockOnly: ${stockOnly ? 'ON' : 'OFF'}`);
      console.log(`Pickup locations loaded: ${pickupLocations?.length || 0} locations`);
    }
    
    // Get filtered locations based on stockOnly setting
    const stockFilteredLocations = stockOnly 
      ? pickupLocations.filter(record => {
          if (isStockOnlyExceptionStore(record)) return true;
          // Only show locations that have products (stock)
          const litAlertsId = record.fields.LitAlertsId;
          if (!litAlertsId) return false;
          
          const locationProducts = locationProductsMap.get(litAlertsId) || [];
          const result = locationProducts.length > 0;
          return result;
        })
      : pickupLocations;
    
    // Apply limit and infiniteScroll logic
    let filteredLocations;
    if (limit && !infiniteScroll) {
      // Show exactly 'limit' number of stores and stop
      filteredLocations = stockFilteredLocations.slice(0, limit);
    } else if (limit && infiniteScroll) {
      // Infinite scroll with 'limit' as page size - show displayCount number of results
      filteredLocations = stockFilteredLocations.slice(0, displayCount);
    } else if (!limit && infiniteScroll) {
      // No limit + infinite scroll = show ALL stores immediately
      filteredLocations = stockFilteredLocations;
    } else {
      // No limit + fixed count = show ALL stores
      filteredLocations = stockFilteredLocations;
    }
    
    return (
      <div className="store-location">
        <h3>{getPickupTitle(stockFilteredLocations.length, props.shopperLocation, limit,
          loadingLocations || loadingStock || (!loadingLocations && stockFilteredLocations.length === 0 && props.shopperLocation?.city))}</h3>
        {filteredLocations.map(record => 
          record.fields.PickupURL && renderLocationRow(record, 'pickup')
        )}
        {infiniteScroll && displayCount < stockFilteredLocations.length && (
          <div className="loading-more">Loading more locations...</div>
        )}
      </div>
    );
  }

  // Add DeliveryOptions component definition
  function DeliveryOptions() {
    // Apply stockOnly filtering to delivery locations
    const stockFilteredDeliveryLocations = stockOnly 
      ? deliveryLocations.filter(record => {
          // Only show delivery locations that have products (stock)
          if (!record.fields.Delivery) return false; // Must have Delivery=true
          
          if (isStockOnlyExceptionStore(record)) return true;
          const litAlertsId = record.fields.LitAlertsId;
          if (!litAlertsId) return false;
          
          const locationProducts = locationProductsMap.get(litAlertsId) || [];
          return locationProducts.length > 0;
        })
      : deliveryLocations.filter(loc => loc.fields.Delivery);
    
    return (
      <div className="store-location">
        <h3>Delivery to {props.shopperLocation?.city || 'Your Location'}</h3>
        {stockFilteredDeliveryLocations.slice(0, displayCount).map(record => 
          renderLocationRow(record, 'delivery')
        )}
        {displayCount < stockFilteredDeliveryLocations.length && (
          <div className="loading-more">Loading more locations...</div>
        )}
      </div>
    );
  }

  function LoadingLocations() {
    const loadingMessage = loadingLocations 
      ? "Loading locations and products..." 
      : loadingStock 
        ? "Checking inventory at stores..." 
        : "Loading...";
        
    return (
      <div className="store-notice">
        <h3>{loadingMessage}</h3>
      </div>
    );
  }

  function NoOptions() {
    const isPickupMode = integrated || (!integrated && props.pickupOrDelivery === 'pickup');
    const isDeliveryMode = !integrated && props.pickupOrDelivery === 'delivery';
    const hasLocation = !!props.shopperLocation?.city;
    return (
      <div className="store-notice">
        <h3>Oops!</h3>
        {stockOnly && isPickupMode ? (
          <>
            <p>No stores with current inventory available.</p>
            <p>Try turning off the "stores with inventory only" filter or check back later.</p>
          </>
        ) : integrated ? (
          <>
            <p>No stores or delivery options found for your location.</p>
            <p>We currently only serve Massachusetts. If you'd like us to bring Good Feels to your state, <a href="mailto:hello@getgoodfeels.com">email us</a>.</p>
          </>
        ) : isDeliveryMode ? (
          <>
            <p>No delivery options available for your location.</p>
            <p>We currently don't deliver to {props.shopperLocation?.city || 'your area'}. If you'd like us to deliver to your area, <a href="mailto:hello@getgoodfeels.com">email us</a>.</p>
          </>
        ) : (
          <>
            <p>No pickup stores found near {hasLocation ? props.shopperLocation.city : 'Your Location'}.</p>
            <p>We currently only serve Massachusetts. If you'd like us to bring Good Feels to your state, <a href="mailto:hello@getgoodfeels.com">email us</a>.</p>
          </>
        )}
      </div>
    );
  }

  // Loading state simplified - we no longer load all products upfront
  // Stock checking happens in the background as stores are loaded
  const isLoading = loadingLocations || (stockOnly && loadingStock);
  
  // No locations found - either none meet filter criteria or none returned from API
  const hasNoResults = !isLoading && (
    (pickupLocations.length === 0 && (!integrated || deliveryLocations.length === 0)) || 
    (stockOnly && !pickupLocations.some(record => {
      if (isStockOnlyExceptionStore(record)) return true;
      const litAlertsId = record.fields.LitAlertsId;
      if (!litAlertsId) return false;
      const locationProducts = locationProductsMap.get(litAlertsId) || [];
      return locationProducts.length > 0;
    }))
  );

  // Check for no results in integrated view
  const hasNoResultsIntegrated = integrated && 
    !isLoading && 
    ((pickupLocations.length === 0 && deliveryLocations.length === 0) || 
     (stockOnly && !pickupLocations.some(record => {
       if (isStockOnlyExceptionStore(record)) return true;
       const litAlertsId = record.fields.LitAlertsId;
       if (!litAlertsId) return false;
       const locationProducts = locationProductsMap.get(litAlertsId) || [];
       return locationProducts.length > 0;
     }) && deliveryLocations.length === 0));

  // In your render, before the main return:
  if (errorState.hasError) {
    return (
      <ProductsUnavailable
        onRetry={() => {
          setErrorState({ hasError: false, message: '' });
          isLocationsFetching.current = false;
          setRetryKey(k => k + 1);
        }}
        retryCountdown={120}
      />
    );
  }

  // Filter out locations with invalid coordinates before sorting
  const validPickupLocations = pickupLocations.filter(
    (loc) => loc && loc.Lat != null && loc.Lng != null && !isNaN(loc.Lat) && !isNaN(loc.Lng)
  );
  // Sort by distance
  validPickupLocations.sort((a, b) => distance(a, props.shopperLocation) - distance(b, props.shopperLocation));

  return (
    <div id="list-of-locations">
      {/* Add the location banner for users outside service area */}
      {isOutsideServiceArea && props.shopperLocation && (
        <LocationBanner
          city={props.shopperLocation.city || ''}
          state={props.shopperLocation.state || ''}
          country={country}
          isVisible={isOutsideServiceArea}
        />
      )}
      
      {/* Add fallback location notice */}
      {props.shopperLocation?.isLocationFallback && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          padding: '10px 15px',
          marginBottom: '15px',
          fontSize: '14px',
          color: '#856404'
        }}>
          <strong>📍 Location Notice:</strong> Unable to auto-detect your location. 
          Showing results near Boston, MA instead.
        </div>
      )}
      
      {isLoading ? (
        <LoadingLocations />
      ) : integrated ? (
        hasNoResultsIntegrated ? (
          <NoOptions />
        ) : (
          <>
            <IntegratedView />
            {/* ProductModal removed in favor of expandable sections */}
          </>
        )
      ) : hasNoResults ? (
        <NoOptions />
      ) : (
        <>
          {props.pickupOrDelivery === 'delivery' ? <DeliveryOptions /> : <PickupOptions />}
          {/* ProductModal removed in favor of expandable sections */}
        </>
      )}
      <div className="widget-version" style={{
        fontSize: '11px', 
        opacity: 0.6, 
        textAlign: 'center', 
        padding: '8px 5px 5px', 
        color: '#aaa',
        borderTop: '1px solid #f0f0f0',
        marginTop: '20px',
        fontFamily: 'monospace'
      }}>
        {process.env.NODE_ENV} - v{process.env.REACT_APP_VERSION || require('../package.json').version}
      </div>
    </div>
  );
}

Locations.propTypes = {
  shopperLocation: PropTypes.object,
  // add other props as needed
};

export default Locations;
